// Scheduler — run telemetry (ADR-0011 PR-6b).
//
// startRun / finishRun / fetchPriorScheduledRuns wrap the /host/runs* API.
// Depends only on the API client, the runtime log leaf, and node:crypto.

import { createHash } from 'node:crypto';
import { api } from '../../api-client.js';
import { type PriorRun } from '@augmented/core';
import { log } from '../runtime.js';









// ---------------------------------------------------------------------------
// Runs telemetry helpers (ENG-4561)
// ---------------------------------------------------------------------------
//
// Wraps /host/runs/start and /host/runs/finish so each Claude Code spawn site
// records a runs row. Best-effort — neither call ever throws to the caller;
// telemetry failures log and return null so the task itself still fires. The
// Claude Code spawn passes AGT_RUN_ID into the child env so MCP tools can
// stamp the run_id onto rows they insert (wiring on the API side lands in a
// follow-up; the env var is harmless until then).

export interface StartRunOptions {
  agent_id: string;
  source_type: 'kanban' | 'scheduled_task' | 'channel' | 'manual' | 'system';
  source_ref?: string;
  channel?: string;
  trace_id?: string;
  metadata?: Record<string, unknown>;
  // ENG-4540 Phase 2: when set + source_type='scheduled_task', the API also
  // creates a kanban card tied to this run. The card id comes back so
  // finishRun can close it. initial_status (ENG-5599) selects the opening
  // column — default 'in_progress' (legacy oneshot), 'todo' for the in-session
  // route where the persistent session picks the card up itself.
  materialize_kanban?: {
    title: string;
    description?: string;
    priority?: number;
    initial_status?: 'todo' | 'in_progress';
    // ENG-6050: completion-notify target carried onto the materialised card so
    // a scheduled task configured to deliver to Teams gets its 👍/👎 rating
    // card posted to that conversation when the card closes.
    notify_channel?: string;
    notify_to?: string;
  };
}

export interface StartRunResult {
  run_id: string | null;
  kanban_item_id: string | null;
}


export async function startRun(opts: StartRunOptions): Promise<StartRunResult> {
  try {
    const res = await api.post<{ run_id?: string; kanban_item_id?: string | null }>(
      '/host/runs/start',
      opts,
    );
    return { run_id: res.run_id ?? null, kanban_item_id: res.kanban_item_id ?? null };
  } catch (err) {
    // Hash the raw error text so durable manager logs don't carry
    // potentially-sensitive payloads (per CLAUDE.md log policy). The
    // 12-char prefix is enough to correlate with server-side logs.
    const errText = err instanceof Error ? err.message : String(err);
    const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
    log(`[runs] start failed for agent_id=${opts.agent_id} source_type=${opts.source_type} error_id=${errId}`);
    return { run_id: null, kanban_item_id: null };
  }
}

export interface FinishRunOptions {
  metadata?: Record<string, unknown>;
  outcomeMessage?: string;
  // ENG-4540 Phase 2: when startRun materialised a kanban card, pass the id
  // back here so the API closes the card to done/failed in lockstep.
  completeKanbanItemId?: string | null;
  result?: string;
}

export async function finishRun(
  runId: string,
  outcome: 'completed' | 'failed' | 'cancelled' | 'timeout',
  options: FinishRunOptions = {},
): Promise<void> {
  try {
    await api.post('/host/runs/finish', {
      run_id: runId,
      outcome,
      outcome_message: options.outcomeMessage,
      metadata: options.metadata,
      complete_kanban_item_id: options.completeKanbanItemId ?? undefined,
      result: options.result,
    });
  } catch (err) {
    const errText = err instanceof Error ? err.message : String(err);
    const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
    log(`[runs] finish failed for run_id=${runId} outcome=${outcome} error_id=${errId}`);
  }
}

// Fetch the recent output text of prior ticks of the same scheduled task so
// the wrapper can inject "here's what you already reported today" context.
// Best-effort — a lookup failure must not block the firing; the agent just
// runs without prior context (same as the pre-injection behaviour).
export const MAX_PRIOR_RUNS = 5;
export async function fetchPriorScheduledRuns(
  agentId: string,
  taskId: string,
): Promise<PriorRun[]> {
  try {
    const data = await api.post<{
      runs?: Array<{ started_at: string; output_text: string | null }>;
    }>('/host/scheduled-tasks/recent-outputs', {
      agent_id: agentId,
      task_id: taskId,
      since_hours: 24,
      limit: MAX_PRIOR_RUNS,
    });
    // Defensive client-side clamp: even though the request asks for
    // `limit: MAX_PRIOR_RUNS`, an upstream regression that ever over-returns
    // would balloon prompt size and break scheduled-task executions. Slice
    // here so the wrapper can never see more than the contracted maximum.
    const rows = Array.isArray(data?.runs) ? data.runs.slice(0, MAX_PRIOR_RUNS) : [];
    return rows
      .filter((r): r is { started_at: string; output_text: string } => typeof r.output_text === 'string' && r.output_text.length > 0)
      .map((r) => ({ startedAt: r.started_at, output: r.output_text }));
  } catch (err) {
    const errText = err instanceof Error ? err.message : String(err);
    // Log a short hash instead of the raw error text — upstream error
    // messages can include row data, JWT fragments, or other sensitive
    // context. Matches the redaction pattern already used elsewhere in
    // this file (see the `errId` log around line 1691).
    const errId = createHash('sha256').update(errText).digest('hex').slice(0, 12);
    log(`[runs] prior-runs lookup failed for task_id=${taskId} error_id=${errId}`);
    return [];
  }
}
